// src/features/procedures/pages/ProceduresListPage.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Description as DescriptionIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  FileCopy as FileCopyIcon,
  NoteAdd as NoteAddIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LocalizedDateDistance from '../../../i18n/LocalizedDateDistance';

// Hooks
import { useProcedures } from '../hooks/useProcedures';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';
import { usePermissions } from '../../auth/hooks/usePermissions';

// Utils
import { UUIDDisplay } from '../../../utils/formatters/uuid';

import PageHeader from '../../../components/common/PageHeader';
import FactCheckIcon from '@mui/icons-material/FactCheck';

export default function ProceduresListPage() {
  const { t } = useTranslation(['procedures', 'common']);
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const permissions = usePermissions();

  // State
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [createMenuAnchor, setCreateMenuAnchor] = useState(null);
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [includeInactive, setIncludeInactive] = useState(false);

  // API hooks
  const {
    procedures,
    loading,
    error,
    clearError,
    fetchProcedures,
    deleteProcedure,
  } = useProcedures();

  // Load initial data
  useEffect(() => {
    fetchProcedures(includeInactive);
  }, [fetchProcedures, includeInactive]);

  // Event handlers
  const handleRefresh = useCallback(() => {
    fetchProcedures(includeInactive);
  }, [fetchProcedures, includeInactive]);

  const handleOpenCreateMenu = useCallback((event) => {
    setCreateMenuAnchor(event.currentTarget);
  }, []);

  const handleCloseCreateMenu = useCallback(() => {
    setCreateMenuAnchor(null);
  }, []);

  const handleCreateFromTemplate = useCallback(() => {
    handleCloseCreateMenu();
    navigate('/procedures/catalog');
  }, [navigate]);

  const handleCreateFromScratch = useCallback(() => {
    handleCloseCreateMenu();
    navigate('/procedures/create');
  }, [navigate]);

  const handleViewProcedure = useCallback((procedure) => {
    navigate(`/procedures/${procedure.procedure_id}`);
  }, [navigate]);

  const handleEditProcedure = useCallback((procedure) => {
    navigate(`/procedures/${procedure.procedure_id}/edit`);
  }, [navigate]);

  const handleMenuOpen = useCallback((event, procedure) => {
    event.stopPropagation();
    setSelectedProcedure(procedure);
    setActionMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setActionMenuAnchor(null);
    setSelectedProcedure(null);
  }, []);

  const handleDeleteProcedure = useCallback((procedure) => {
    showConfirmDialog({
      title: t('procedures:confirm.delete_procedure_title'),
      message: t('procedures:confirm.delete_procedure_message', { name: procedure.name }),
      confirmText: t('procedures:confirm.delete_confirm'),
      cancelText: t('common:buttons.cancel'),
      severity: 'error',
      onConfirm: async () => {
        try {
          const result = await deleteProcedure(procedure.procedure_id);
          if (result.success) {
            showSuccess(t('procedures:success.procedure_deleted'));
          } else {
            showError(result.error || t('procedures:errors.delete_failed'));
          }
        } catch (error) {
          showError(t('procedures:errors.delete_failed'));
        }
      },
    });
    handleMenuClose();
  }, [showConfirmDialog, deleteProcedure, showSuccess, showError, handleMenuClose, t]);

  // Check permissions
  const canCreate = permissions.hasScope('procedure:create');
  const canUpdate = permissions.hasScope('procedure:update');
  const canDelete = permissions.hasScope('procedure:delete');

  // DataGrid columns
  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: t('procedures:grid.columns.name'),
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={2} sx={{ py: 1 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
            <DescriptionIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {params.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('common:labels.id')} <UUIDDisplay uuid={params.row.procedure_id} />
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: t('procedures:grid.columns.description'),
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'step_count',
      headerName: t('procedures:grid.columns.steps'),
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value || 0}
        </Typography>
      ),
    },
    {
      field: 'is_system_procedure',
      headerName: t('procedures:grid.columns.type'),
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('procedures:labels.system') : t('procedures:labels.custom')}
          size="small"
          color={params.value ? 'secondary' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'is_active',
      headerName: t('procedures:grid.columns.status'),
      width: 120,
      renderCell: (params) => (
        <Chip
          icon={params.value ? <ActiveIcon /> : <InactiveIcon />}
          label={params.value ? t('procedures:labels.active') : t('procedures:labels.inactive')}
          size="small"
          color={params.value ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: t('procedures:grid.columns.created'),
      width: 140,
      renderCell: (params) => (
        <Typography variant="caption" color="text.secondary">
          {params.value ? <LocalizedDateDistance date={params.value} /> : t('procedures:grid.columns.unknown')}
        </Typography>
      ),
    },
    {
      field: 'actions',
      // Icon-only kebab column: no header, like the other list grids
      headerName: '',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuOpen(e, params.row)}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ], [handleMenuOpen, t]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Header */}
      <PageHeader
        icon={FactCheckIcon}
        title={t('procedures:title')}
        subtitle={t('procedures:subtitle')}
        actions={<>
          <Tooltip title={t('procedures:actions.refresh')}>
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          {canCreate && (
            <>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                endIcon={<ArrowDropDownIcon />}
                onClick={handleOpenCreateMenu}
                disabled={loading}
              >
                {t('procedures:actions.add_procedure')}
              </Button>
              <Menu
                anchorEl={createMenuAnchor}
                open={Boolean(createMenuAnchor)}
                onClose={handleCloseCreateMenu}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem onClick={handleCreateFromTemplate}>
                  <ListItemIcon>
                    <FileCopyIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>
                    {t('procedures:actions.list_page.create_from_template')}
                  </ListItemText>
                </MenuItem>
                <MenuItem onClick={handleCreateFromScratch}>
                  <ListItemIcon>
                    <NoteAddIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>
                    {t('procedures:actions.list_page.create_from_scratch')}
                  </ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </>}
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Filter Controls */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button
          variant={includeInactive ? 'contained' : 'outlined'}
          size="small"
          onClick={() => setIncludeInactive(!includeInactive)}
        >
          {includeInactive ? t('procedures:filters.showing_all') : t('procedures:filters.showing_active_only')}
        </Button>
      </Box>

      {/* DataGrid */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          '& .MuiDataGrid-root': {
            border: 'none',
            '& .MuiDataGrid-main': {
              '& .MuiDataGrid-columnHeaders': {
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                '&:focus': {
                  outline: 'none',
                },
              },
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              },
            },
          },
        }}
      >
        <DataGrid
          rows={procedures}
          columns={columns}
          getRowId={(row) => row.procedure_id}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          disableColumnMenu
          onRowClick={(params) => handleViewProcedure(params.row)}
          rowHeight={72}
          getRowHeight={() => 'auto'}
          slots={{
            noRowsOverlay: () => (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
                py={4}
              >
                <DescriptionIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('procedures:messages.no_procedures')}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" paragraph>
                  {t('procedures:messages.no_procedures_description')}
                </Typography>
                {canCreate && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={handleOpenCreateMenu}
                  >
                    {t('procedures:messages.create_first_procedure')}
                  </Button>
                )}
              </Box>
            ),
          }}
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid',
              borderColor: 'divider',
            },
          }}
        />
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
        }}
      >
        <MenuItem onClick={() => { handleViewProcedure(selectedProcedure); handleMenuClose(); }}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('procedures:actions.view_details')}</ListItemText>
        </MenuItem>

        {canUpdate && !selectedProcedure?.is_system_procedure && (
          <MenuItem onClick={() => { handleEditProcedure(selectedProcedure); handleMenuClose(); }}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('procedures:actions.edit')}</ListItemText>
          </MenuItem>
        )}

        {canDelete && !selectedProcedure?.is_system_procedure && (
          <MenuItem onClick={() => { handleDeleteProcedure(selectedProcedure); handleMenuClose(); }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>
              <Typography color="error">{t('procedures:actions.delete')}</Typography>
            </ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Confirm Dialog */}
      <ConfirmDialog />
    </Box>
  );
}